import { defineStore } from 'pinia'
import { useStoreConfig } from '~/store/storeConfig'
import { useLanguageStore } from '~/store/language'
import BuildCategoryTree from '~/utils/categories/BuildCategoryTree'
import {
  type CategoryItem,
  type CategoryTree,
  type FlagItem,
  FlagType,
  type SearchDocument,
  type OfferItem,
} from '~/types/searchTypes'
import fetchFlagsByLang from '~/utils/category/fetchFlagsByLang'
import { useCategoriesByKeysStore } from '~/store/categoriesByKeysStore'
import { fetchRetryParameters, requestHostHeaders, type RequestHostHeadersType } from '~/composables/constants/api'

function getInitialState(): ICategoryStoreState {
  return {
    categoryTree: [],
    categories: [],
    flags: [],
    offers: [],
    isLoading: false,
    isFetching: false,
  }
}

export const useCategoryStore = defineStore('categoryStore', {
  state: (): ICategoryStoreState => getInitialState(),
  getters: {
    getCategoryTree(state): CategoryTree[] {
      return state.categoryTree
    },
    getCategories(state): CategoryItem[] {
      return state.categories
    },
    getFlags(state): FlagItem[] {
      return state.flags
    },
    getExclusiveOffers(state): FlagItem[] {
      return state.flags.filter((flag) => !flag.type || String(flag.type) === FlagType.Flag)
    },
    getAllOffers(state): OfferItem[] {
      return state.offers
    },
    loading(state): boolean {
      return state.isLoading
    },
    fetching(state): boolean {
      return state.isFetching
    },
  },
  actions: {
    setLoading(value: boolean): void {
      this.isLoading = value
    },
    setFetching(value: boolean): void {
      this.isFetching = value
    },
    async getFlagsByLocale(locale: string) {
      const langStore = useLanguageStore()

      const flags: FlagItem[] = await fetchFlagsByLang(langStore.getLanguageId, locale)

      this.flags = [...flags.sort((a: FlagItem, b: FlagItem) => a.position - b.position)]

      await this.getOffers(locale)
    },
    async getOffers(locale: string) {
      const offerFlags = this.flags.filter((flag) => String(flag.type) === FlagType.Offer)

      const flagRequests = offerFlags.map((flag) => {
        return $fetch<SearchDocument>('/api/search/searchForCategoryItems', {
          method: 'POST',
          body: {
            locale,
            params: {
              'f.flag_ids': String(flag.id),
            },
            categoryId: null,
            limit: 5,
            offset: 0,
          },
          ...fetchRetryParameters,
          headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
        })
      })

      const flagProducts = await Promise.all(flagRequests)

      this.offers = offerFlags.map((flag, index) => ({
        ...flag,
        images: flagProducts[index].items.map((product) => product.image),
      }))
    },
    async getCategoriesByLocale(locale: string) {
      this.setFetching(true)
      const storeConfig = useStoreConfig()
      await storeConfig.getStoreConfig()
      const categoryDocKey: string = storeConfig.getCategoryDocKey

      const categoriesByKeysStore = useCategoriesByKeysStore()
      const categories: CategoryItem[] =
        (await categoriesByKeysStore.getByCategoryDocAndLocale(categoryDocKey, locale)) ?? []

      this.categories = categories

      this.categoryTree = BuildCategoryTree(null, [...categories])
      this.setFetching(false)
    },
  },
})

export interface ICategoryStoreState {
  categoryTree: CategoryTree[]
  categories: CategoryItem[]
  flags: FlagItem[]
  offers: OfferItem[]
  isLoading: boolean
  isFetching: boolean
}
